import { useCallback, useEffect } from "react"
import { useUserLocalStorage } from "@/routes/app/-components/use-user-local-storage"
import { useGetCurrentDateTimeAsString } from "./use-get-current-date-time-as-string"
import { useQueryClient } from "@tanstack/react-query"
import { useLocation } from "@tanstack/react-router"
import { useTranslation } from "react-i18next"
import {
  useCreateReminder,
  CreateReminderRequest,
  EditReminderRequest,
} from "@/orval/gen"
import { DefaultModal } from "@components/default-modal"
import { useForm } from "@mantine/form"
import { LoadingOverlay } from "@mantine/core"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { CreateOrEditReminderForm } from "./-components/CreateOrEditReminderForm"
import { invalidateReminderQueries } from "./invalidate-reminder-queries"

type ReminderFormValues = CreateReminderRequest | EditReminderRequest

interface ReminderInputModalProps {
  opened: boolean
  onClose: () => void
}

function ReminderInputModalContent({
  opened,
  onClose,
}: ReminderInputModalProps) {
  const { t } = useTranslation("app_server")
  const queryClient = useQueryClient()
  const location = useLocation().pathname
  const nowString = useGetCurrentDateTimeAsString(1000)

  const [selectedOwnerGroupId, _setSelectedOwnerGroupId] = useUserLocalStorage<
    number | null | undefined
  >({
    key: "reminder-input-owner-group-id",
    defaultValue: undefined,
  })

  const { mutateAsync: createReminder, isPending } = useCreateReminder({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        invalidateReminderQueries(queryClient)
        onClose()
      },
    },
  })

  const getInitialDateTime = () => {
    if (
      typeof nowString === "string" &&
      nowString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)
    ) {
      return nowString
    }
    try {
      return new Date(nowString).toISOString().slice(0, 16)
    } catch {
      return new Date().toISOString().slice(0, 16)
    }
  }

  function determineObjectType(): number {
    if (location.includes("persons/")) {
      return 1
    } else if (location.includes("activities/")) {
      return 2
    } else if (location.includes("cast/")) {
      return 3
    } else if (location.includes("contracts/")) {
      return 4
    } else if (location.includes("productions/")) {
      return 5
    }
    return 1
  }

  function determineObjectId(): number {
    const path = location.split("?")[0]
    const idString = path.substring(path.lastIndexOf("/") + 1)
    const id = Number(idString)
    if (!isNaN(id) && id > 0) return id
    return 0
  }

  const form = useForm<ReminderFormValues>({
    mode: "uncontrolled",
    initialValues: {
      userId: 0,
      objectId: determineObjectId(),
      objectTypeId: determineObjectType(),
      time: getInitialDateTime() as string,
      completed: false,
      text: "",
      ownerGroupId: selectedOwnerGroupId ?? 0,
    },
  })

  useEffect(() => {
    if (opened) {
      form.setValues({
        userId: 0,
        objectId: determineObjectId(),
        objectTypeId: determineObjectType(),
        time: getInitialDateTime() as string,
        completed: false,
        text: "",
        ownerGroupId: selectedOwnerGroupId ?? 0,
      })
    }
  }, [opened])

  const handleSubmit = useCallback(
    async (values: ReminderFormValues) => {
      if (!values.userId || values.userId === 0) {
        form.setErrors({
          userId: t("AssignRequiredError") ?? "Assign is required",
        })
        return
      }
      if (!values.ownerGroupId || values.ownerGroupId === 0) {
        form.setErrors({
          ownerGroupId: t("OwnerRequiredError") ?? "Owner is required",
        })
        return
      }
      const createRequest = values as CreateReminderRequest
      await createReminder({ data: createRequest })
    },
    [createReminder, form, t]
  )

  return (
    <div style={{ position: "relative" }}>
      <LoadingOverlay visible={isPending} />
      <CreateOrEditReminderForm
        form={form}
        handleSubmit={handleSubmit}
        onCancel={onClose}
        isSubmitting={isPending}
      />
    </div>
  )
}

export function ReminderInputModal(props: ReminderInputModalProps) {
  const { t } = useTranslation("app_server")
  return (
    <DefaultModal
      opened={props.opened}
      onClose={props.onClose}
      title={t("NewReminder")}
      centered
    >
      <ReminderInputModalContent {...props} />
    </DefaultModal>
  )
}
